import React, { Component } from 'react';
import { Button, Form, Radio, Select } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { FormInstance } from 'antd/lib/form';
import { connect } from 'react-redux';
import cx from 'classnames';

import { ReactComponent as ChevronDownIconSVG } from '../../../../assets/sightx-icons/svg/icon-chevron.svg';
import { RootState } from '../../../../slices/rootReducer';
import {
  selectIssuingProUpgradeState,
  selectUpgradeAccountError
} from '../../../../slices/upgradeAccount/upgradeToPro.selectors';
import { getSubscriptionPrices } from '../../../../slices/upgradeAccount/subscriptionPrices/thunk';
import {
  IIssueProfessionalSubscriptionDTO,
  issueProfessionalSubscription
} from '../../../../slices/upgradeAccount/upgradeToPro.thunk';
import { CadenceTypesValues, MONTHS_IN_A_YEAR, pricePerCadence } from '../../../auth/proRegister/subscriptionTypes';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';
import { PromotionalCode } from './promotionalCode';
import { actions } from '../../../../slices/upgradeAccount/promotionalCode/index';
import { getIsValidatingCode, getPromotionalDiscount, getValidationErrorMessage } from '../../../../slices/upgradeAccount/promotionalCode/selectors';
import { BlockedByFeature } from '../../../../featureToggles/features/utils';
import { IPromotionalCodeDiscount } from '../../../../slices/upgradeAccount/promotionalCode/interfaces';
import { FeatureToggle, Off, On } from '../../../../featureToggles';
import { selectSubscriptionPrices } from '../../../../slices/upgradeAccount/subscriptionPrices/selectors';
import { ISubscriptionPrices } from '../../../../slices/upgradeAccount/subscriptionPrices/interfaces';
import { formatPriceAmount, getDiscountedPrice, getPriceByCadence } from './utils';

import styles from './usersAndCadence.module.scss';

const isTiersEnabled = isFeatureEnabled(Features.TrialsOfTiers);

interface IUsersAndCadenceState {
  subscriptionCadence: CadenceTypesValues;
  numberOfUsers: number;
  promotionalCode: string | undefined;
}

interface IUsersAndCadenceProps {
  nextAction: (subscription: IIssueProfessionalSubscriptionDTO) => void;
  previousAction: () => void;
  isConfirmation: boolean;
  onSubmit: () => void;
}

interface IMapDispatchProps {
  issueProfessionalSubscription: (arg: IIssueProfessionalSubscriptionDTO) => ReturnType<ReturnType<typeof issueProfessionalSubscription>>;
  getSubscriptionPrices: () => ReturnType<ReturnType<typeof getSubscriptionPrices>>;
  resetState: () => void;
}

interface IMapStateProps {
  issuingProSubscription: boolean;
  errorIssuingProSubscription: string | null;
  // Use for Feature.PromotionalCodes
  discount: IPromotionalCodeDiscount | undefined;
  errorMessage: string | undefined;
  isValidating: boolean;
  prices: ISubscriptionPrices | undefined;
  // End of use for Feature.PromotionalCodes
}

type Props = IUsersAndCadenceProps & IMapDispatchProps & IMapStateProps;

class UsersAndCadenceBase extends Component<Props, IUsersAndCadenceState> {
  private formRef: React.RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      numberOfUsers: 1,
      subscriptionCadence: CadenceTypesValues.ANNUALLY,
      promotionalCode: undefined
    };

    this.formRef = React.createRef<FormInstance>();
    this.handlePromotionalCodeChange = this.handlePromotionalCodeChange.bind(this);
    this.onChangeNumberOfUsers = this.onChangeNumberOfUsers.bind(this);
    this.onChangeBillingCadence = this.onChangeBillingCadence.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onConfirm = this.onConfirm.bind(this);
    this.handleApplyCode = this.handleApplyCode.bind(this);
  }

  componentDidMount(): void {
    this.props.getSubscriptionPrices();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    const { issuingProSubscription, errorIssuingProSubscription } = this.props;
    const { issuingProSubscription: prevIssuingProSubscription } = prevProps;

    if (!issuingProSubscription && prevIssuingProSubscription && !errorIssuingProSubscription) {
      const { promotionalCode, subscriptionCadence, numberOfUsers } = this.state;
      this.props.nextAction({
        subscriptionCadence,
        numberOfUsers,
        promoCode: promotionalCode
      });
    }
  }

  private handleApplyCode() {
    if (!isFeatureEnabled(Features.PromotionalCodes)) {
      return;
    }
    const currentForm = this.formRef.current;
    if (currentForm) {
      currentForm?.validateFields(['promotionalCode']);
    }
  }

  private handlePromotionalCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!isFeatureEnabled(Features.PromotionalCodes)) {
      return;
    }
    const { discount, errorMessage } = this.props;
    if (discount || errorMessage) {
      this.props.resetState();
    }
    this.setState({ promotionalCode: event.currentTarget.value });
  }

  private onChangeNumberOfUsers(numberOfUsers: number) {
    this.setState({ numberOfUsers });
  }

  private onChangeBillingCadence(e: RadioChangeEvent) {
    const subscriptionCadence = e.target.value;
    this.setState({ subscriptionCadence });
  }

  private onSubmit() {
    if (isFeatureEnabled(Features.PromotionalCodes) && this.props.isValidating) {
      return;
    }
    this.props.onSubmit();
  }

  private onConfirm() {
    if (isFeatureEnabled(Features.PromotionalCodes) && this.props.isValidating) {
      return;
    }
    const { promotionalCode, subscriptionCadence, numberOfUsers } = this.state;
    this.props.issueProfessionalSubscription({
      subscriptionCadence,
      numberOfUsers,
      promoCode: promotionalCode
    });
  }

  private renderDiscount() {
    const { discount } = this.props;
    if (!isFeatureEnabled(Features.PromotionalCodes) || !discount) {
      return null;
    }
    const percentAmount = discount.percentOff ? `${formatPriceAmount(discount.percentOff)}%` : undefined;
    const amount = discount.amountOffInCents ? `$${formatPriceAmount(discount.amountOffInCents / 100)}` : undefined;
    return (
      <div className={styles.discount}>{`${amount || percentAmount} discount applied`}</div>
    );
  }

  private renderAmountToPay(price: number) {
    if (!isFeatureEnabled(Features.PromotionalCodes)) {
      return (
        <div className={styles.price}>
          {`$${price}`}
        </div>
      );
    }
    const { discount } = this.props;
    const currentPrice = getDiscountedPrice(price, discount);
    return <>
      {
        !!discount &&
        <div className={styles.discountedPrice}>
          {`$${formatPriceAmount(price)}`}
        </div>
      }
      <div className={styles.price}>
        {`$${currentPrice}`}
      </div>
    </>;
  }

  private renderPrice() {
    const { prices, discount } = this.props;
    const { numberOfUsers, subscriptionCadence } = this.state;
    const basePrice = isFeatureEnabled(Features.PromotionalCodes) ? getPriceByCadence(subscriptionCadence, prices) : pricePerCadence[subscriptionCadence];
    const price = basePrice * numberOfUsers;
    const isMonthly = subscriptionCadence === CadenceTypesValues.MONTHLY;
    const annualSavings = prices?.annualSavings || 0;
    const savingsPerUser = isFeatureEnabled(Features.PromotionalCodes) ? annualSavings / 100 : (pricePerCadence[CadenceTypesValues.MONTHLY] * MONTHS_IN_A_YEAR) - pricePerCadence[CadenceTypesValues.ANNUALLY];
    const savingsText = isFeatureEnabled(Features.PromotionalCodes) && !!discount ? 'by selecting annual' : 'in savings';
    const initialText = isFeatureEnabled(Features.PromotionalCodes) ? 'Save $' : '';
    return <div className={cx(styles.pricing, {
      [styles[Features.PromotionalCodes]]: isFeatureEnabled(Features.PromotionalCodes)
    })}>
      {!isMonthly && <div className={styles.savings}>
        {`${initialText}${formatPriceAmount(savingsPerUser * numberOfUsers)} ${savingsText}`}
      </div>}
      {this.renderDiscount()}
      <div className={styles.amountBilled}>
        {this.renderAmountToPay(price)}
        <FeatureToggle feature={Features.PromotionalCodes}>
          <On/>
          <Off>
            <div className={styles.label}>
              Billed
              <br />
              {subscriptionCadence}
            </div>
          </Off>
        </FeatureToggle>
      </div>
    </div>;
  }

  private renderPromotionalCode() {
    if (!isFeatureEnabled(Features.PromotionalCodes)) {
      return null;
    }
    const { promotionalCode } = this.state;
    return (
      <PromotionalCode
        code={promotionalCode}
        onApply={this.handleApplyCode}
        onChange={this.handlePromotionalCodeChange}
      />
    );
  }

  private renderSelectNumberOfUsers() {
    const additionalInfo = isTiersEnabled ? 'Professional plans are limited to three users' : 'Additional users can be added later';
    return (
      <div className={cx({ [styles.field]: isFeatureEnabled(Features.PromotionalCodes) })}>
        <div className={styles.selectNumberOfUser}>Select number of users</div>
        <Form.Item name="numberOfUsers" rules={[{ required: true }]}>
          <Select
            className={styles.select}
            options={[1, 2, 3].map(value => ({ value }))}
            suffixIcon={<ChevronDownIconSVG />}
            onChange={this.onChangeNumberOfUsers}
          />
        </Form.Item>
        <div className={cx(styles.inputAdditionalInfo,
          { [styles[Features.PromotionalCodes]]: isFeatureEnabled(Features.PromotionalCodes) }
        )}>{additionalInfo}</div>
      </div>
    );
  }

  private renderTopRow() {
    if (!isFeatureEnabled(Features.PromotionalCodes)) {
      return this.renderSelectNumberOfUsers();
    }
    return (
      <div className={styles.row}>
        {this.renderSelectNumberOfUsers()}
        {this.renderPromotionalCode()}
      </div>
    );
  }

  renderForm() {
    const annualText = isFeatureEnabled(Features.PromotionalCodes) ? 'Annual' : 'Annually';
    return <div className={cx(styles.upgradeToProForm, { [styles.tiers]: isTiersEnabled })}>
      <Form
        ref={this.formRef}
        onFinish={this.onSubmit}
        initialValues={{
          numberOfUsers: this.state.numberOfUsers,
          promotionalCode: this.state.promotionalCode,
          subscriptionCadence: this.state.subscriptionCadence || CadenceTypesValues.ANNUALLY
        }}
      >
        {this.renderTopRow()}
        <div className={cx(styles.selectBillingCadence, {
          [styles[Features.PromotionalCodes]]: isFeatureEnabled(Features.PromotionalCodes)
        })}>Select billing cadence</div>
        <Form.Item name="subscriptionCadence" rules={[{ required: true }]}>
          <Radio.Group
            className={styles.billingCadence}
            onChange={this.onChangeBillingCadence}
          >
            <Radio.Button value={CadenceTypesValues.ANNUALLY}>
              {annualText}
            </Radio.Button>
            <Radio.Button value={CadenceTypesValues.MONTHLY}>
              Monthly
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
        {this.renderPrice()}
        {this.renderActions()}
      </Form>
    </div>;
  }

  renderActions() {
    const { promotionalCode } = this.state;
    const { issuingProSubscription, isConfirmation, isValidating, discount } = this.props;
    const isPromotionalCodeValid = promotionalCode ? !!discount : true;
    const textButton = isFeatureEnabled(Features.PromotionalCodes) && isConfirmation ? 'Back' : 'Cancel';
    return <div className={cx(styles.submitButton, { [styles.tiers]: isTiersEnabled })}>
      <Button
        type="link"
        onClick={this.props.previousAction}
        disabled={issuingProSubscription}
        className={styles.backButton}
      >
        <span>{textButton}</span>
      </Button>
      <Button
        htmlType="submit"
        onClick={isConfirmation ? this.onConfirm : undefined}
        type="primary"
        disabled={issuingProSubscription || isValidating || !isPromotionalCodeValid}
        loading={issuingProSubscription}
      >
        {isConfirmation ? 'Proceed to payment' : 'Next'}
      </Button>
    </div>;

  }

  renderConfirmation() {
    return <>
      <div className={cx(styles.confirmation, { [styles.tiers]: isTiersEnabled })}>
        You are about to upgrade to a SightX Professional plan 🎉
        <br />
        <br />
        To complete the upgrade, we'll need to log you out of your Lite account and close any live campaigns in your projects.
        <br />
        <br />
        When you log back in, you'll land in your new Pro account, with all of your projects and data available.
      </div>
      {this.renderActions()}
    </>;
  }

  render(): React.ReactNode {
    const { isConfirmation } = this.props;
    if (isConfirmation) {
      return this.renderConfirmation();
    }
    return this.renderForm();
  }
}

const mapStateToProps = (state: RootState): IMapStateProps => {
  const discountResult = getPromotionalDiscount(state);
  const errorMessageResult = getValidationErrorMessage(state);
  const isValidatingResult = getIsValidatingCode(state);
  const pricesResult = selectSubscriptionPrices(state);

  return {
    issuingProSubscription: selectIssuingProUpgradeState(state),
    errorIssuingProSubscription: selectUpgradeAccountError(state),
    discount: discountResult !== BlockedByFeature ? discountResult : undefined,
    errorMessage: errorMessageResult !== BlockedByFeature ? errorMessageResult : undefined,
    isValidating: isValidatingResult !== BlockedByFeature ? isValidatingResult : false,
    prices: pricesResult !== BlockedByFeature ? pricesResult : undefined
  };
};

const mapDispatchToProps = {
  issueProfessionalSubscription,
  getSubscriptionPrices,
  resetState: actions.resetState
};

export const UsersAndCadence = connect(mapStateToProps, mapDispatchToProps)(UsersAndCadenceBase);

